// AssignBulkTeachersModal.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import useClassesStore from '../../../stateManagers/store/useClassesStore';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { useSchoolDetailsStore } from '../../../stateManagers/hooks/useSchoolDetailsStore';

/* ─── Types ──────────────────────────────────────────────────────────── */
interface SubjectData {
  subject_id: string;
  subject_name: string;
  class_id: string;
  class_name?: string;
  subject_type?: string;
  status?: string;
}

interface TeacherData {
  id: string;
  name: string;
}

interface ClassOption {
  id: string;
  value: string;
  label: string;
  sections?: string[];
}

interface ClassEntry {
  class_id: string;
  selectedClassValue: string;
  section: string;
  subject_id: string;
  errors: Record<string, string>;
}

interface AssignmentRow {
  class_id: string;
  selectedClassValue: string;
  sections: string[];
  teacher_id: string;
  subject_id: string;
  errors: Record<string, string>;
}

type ModalMode = 'separate' | 'shared';

interface AssignBulkTeachersModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/* ─── Helpers ─────────────────────────────────────────────────────── */
const emptyRow = (): AssignmentRow => ({
  class_id: '',
  selectedClassValue: '',
  sections: [],
  teacher_id: '',
  subject_id: '',
  errors: {},
});

const emptyClassEntry = (): ClassEntry => ({
  class_id: '',
  selectedClassValue: '',
  section: '',
  subject_id: '',
  errors: {},
});

const getId = (item: any): string => {
  return String(item.id || item._id || item.ID || item.subject_id || item.class_id || '');
};

/* ══════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════════ */
const AssignBulkTeachersModal: React.FC<AssignBulkTeachersModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const toast = useRef<Toast>(null);
  const { classOptions, fetchClasses } = useClassesStore();
  const { schoolDetails } = useSchoolDetailsStore();

  /* ── Mode ── */
  const [mode, setMode] = useState<ModalMode>('separate');

  /* ── Separate-teacher mode state ── */
  const [assignments, setAssignments] = useState<AssignmentRow[]>([emptyRow()]);
  const [subjectsByRow, setSubjectsByRow] = useState<Record<number, SubjectData[]>>({});
  const [subjectsLoading, setSubjectsLoading] = useState<Record<number, boolean>>({});

  /* ── Shared-teacher mode state ── */
  const [sharedTeacherId, setSharedTeacherId] = useState('');
  const [classEntries, setClassEntries] = useState<ClassEntry[]>([emptyClassEntry()]);
  const [subjectsByEntry, setSubjectsByEntry] = useState<Record<number, SubjectData[]>>({});
  const [subjectsLoadingEntry, setSubjectsLoadingEntry] = useState<Record<number, boolean>>({});

  /* ── Common ── */
  const [teachers, setTeachers] = useState<TeacherData[]>([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (open) {
      fetchClasses(true);
      fetchTeachers();
      resetAll();
    }
  }, [open]);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.assign-modal .p-dialog');
      const mask = document.querySelector('.p-dialog-mask.assign-modal');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const fetchTeachers = async () => {
    setTeachersLoading(true);
    try {
      const res = await apiClient.get('/api/assigned-teachers/teachers-with-assignments', { 
        params: { page: 1, limit: 100 } 
      });
      if (res.data.status === 'success') {
        const teachersData = (res.data.data || []).map((t: any) => ({
          id: getId(t),
          name: t.name || '',
        }));
        setTeachers(teachersData);
      } else {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: res.data.message || 'Failed to load teachers', life: 4000 });
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.message || 'Failed to load teachers', life: 4000 });
    } finally {
      setTeachersLoading(false);
    }
  };

  /* ─── Subject fetchers ─────────────────────────────────────────── */
  const fetchSubjectsForRow = useCallback(async (classId: string, rowIdx: number) => {
    setSubjectsLoading(p => ({ ...p, [rowIdx]: true }));
    try {
      const res = await apiClient.get('/subjects_by_class', { 
        params: { class_id: classId } 
      });
      
      if (res.data.status === 'success') {
        const subjects = (res.data.subjects || []).map((s: any) => ({
          subject_id: s.subject_id || getId(s),
          subject_name: s.subject_name || '',
          class_id: s.class_id || classId,
          class_name: s.class_name || '',
          subject_type: s.subject_type || 'regular',
          status: s.status || 'active',
        }));
        setSubjectsByRow(p => ({ ...p, [rowIdx]: subjects }));
      } else {
        setSubjectsByRow(p => ({ ...p, [rowIdx]: [] }));
      }
    } catch (error) {
      console.error('❌ Error fetching subjects:', error);
      setSubjectsByRow(p => ({ ...p, [rowIdx]: [] }));
    } finally { 
      setSubjectsLoading(p => ({ ...p, [rowIdx]: false })); 
    }
  }, []);

  const fetchSubjectsForEntry = useCallback(async (classId: string, entryIdx: number) => {
    setSubjectsLoadingEntry(p => ({ ...p, [entryIdx]: true }));
    try {
      const res = await apiClient.get('/subjects_by_class', { 
        params: { class_id: classId } 
      });
      
      if (res.data.status === 'success') {
        const subjects = (res.data.subjects || []).map((s: any) => ({
          subject_id: s.subject_id || getId(s),
          subject_name: s.subject_name || '',
          class_id: s.class_id || classId,
          class_name: s.class_name || '',
          subject_type: s.subject_type || 'regular',
          status: s.status || 'active',
        }));
        setSubjectsByEntry(p => ({ ...p, [entryIdx]: subjects }));
      } else {
        setSubjectsByEntry(p => ({ ...p, [entryIdx]: [] }));
      }
    } catch { 
      setSubjectsByEntry(p => ({ ...p, [entryIdx]: [] })); 
    } finally { 
      setSubjectsLoadingEntry(p => ({ ...p, [entryIdx]: false })); 
    }
  }, []);

  /* ─── Separate-teacher row helpers ────────────────────────────── */
  const updateRow = (idx: number, patch: Partial<AssignmentRow>) => {
    setAssignments(prev =>
      prev.map((row, i) =>
        i === idx
          ? { ...row, ...patch, errors: { ...row.errors, ...Object.fromEntries(Object.keys(patch).map(k => [k, ''])) } }
          : row
      )
    );
  };

  const handleRowClassChange = (idx: number, value: string) => {
    const cls = classOptions.find(o => o.value === value) as ClassOption | undefined;
    updateRow(idx, { class_id: cls?.id || '', selectedClassValue: value, sections: [], subject_id: '' });
    setSubjectsByRow(p => { const n = { ...p }; delete n[idx]; return n; });
    if (cls?.id) fetchSubjectsForRow(cls.id, idx);
  };

  const handleSectionToggle = (idx: number, section: string, checked: boolean) => {
    const current = assignments[idx].sections;
    updateRow(idx, { sections: checked ? [...current, section] : current.filter(s => s !== section) });
  };

  const addRow = () => setAssignments(p => [...p, emptyRow()]);
  const removeRow = (idx: number) => {
    setAssignments(p => p.filter((_, i) => i !== idx));
    setSubjectsByRow(p => { const n = { ...p }; delete n[idx]; return n; });
  };

  /* ─── Shared-teacher entry helpers ────────────────────────────── */
  const updateEntry = (idx: number, patch: Partial<ClassEntry>) => {
    setClassEntries(prev =>
      prev.map((entry, i) =>
        i === idx
          ? { ...entry, ...patch, errors: { ...entry.errors, ...Object.fromEntries(Object.keys(patch).map(k => [k, ''])) } }
          : entry
      )
    );
  };

  const handleEntryClassChip = (idx: number, value: string) => {
    const cls = classOptions.find(o => o.value === value) as ClassOption | undefined;
    if (classEntries[idx].selectedClassValue === value) {
      updateEntry(idx, { class_id: '', selectedClassValue: '', section: '', subject_id: '' });
      setSubjectsByEntry(p => { const n = { ...p }; delete n[idx]; return n; });
      return;
    }
    updateEntry(idx, { class_id: cls?.id || '', selectedClassValue: value, section: '', subject_id: '' });
    setSubjectsByEntry(p => { const n = { ...p }; delete n[idx]; return n; });
    if (cls?.id) fetchSubjectsForEntry(cls.id, idx);
  };

  const addEntry = () => setClassEntries(p => [...p, emptyClassEntry()]);
  const removeEntry = (idx: number) => {
    setClassEntries(p => p.filter((_, i) => i !== idx));
    setSubjectsByEntry(p => { const n = { ...p }; delete n[idx]; return n; });
  };

  /* ─── Mode switch ──────────────────────────────────────────────── */
  const switchMode = (m: ModalMode) => {
    setMode(m);
  };

  const resetAll = () => {
    setMode('separate');
    setAssignments([emptyRow()]);
    setSubjectsByRow({});
    setSharedTeacherId('');
    setClassEntries([emptyClassEntry()]);
    setSubjectsByEntry({});
  };

  /* ─── Submit ───────────────────────────────────────────────────── */
  const handleSubmit = async () => {
    let valid = true;
    let payload: any;

    // ✅ Get session from schoolDetails
    const currentSession = schoolDetails?.current_session;
    
    if (!currentSession) {
      toast.current?.show({ 
        severity: 'warn', 
        summary: 'Session Required', 
        detail: 'No current session found. Please set a current session first.', 
        life: 3000 
      });
      return;
    }

    const sessionId = currentSession.id;
    const sessionName = currentSession.session;

    console.log('📌 Using Session:', { sessionId, sessionName });

    if (mode === 'separate') {
      const validated = assignments.map(row => {
        const errors: Record<string, string> = {};
        if (!row.class_id) { errors.class_id = 'Select a class'; valid = false; }
        if (!row.subject_id) { errors.subject_id = 'Select a subject'; valid = false; }
        if (!row.teacher_id) { errors.teacher_id = 'Select a teacher'; valid = false; }
        return { ...row, errors };
      });
      setAssignments(validated);
      if (!valid) { 
        toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please fill all required fields', life: 3000 }); 
        return; 
      }
      
      // ✅ Build payload with session_id and session
      payload = {
        assignments: validated.map(row => ({
          session_id: sessionId,
          session: sessionName,
          class_id: row.class_id,
          sections: row.sections.length > 0 ? row.sections : [],
          teacher_id: row.teacher_id,
          subject_id: row.subject_id,
        })),
      };
    } else {
      // shared mode
      if (!sharedTeacherId) {
        toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please select a teacher', life: 3000 });
        return;
      }
      const validated = classEntries.map(entry => {
        const errors: Record<string, string> = {};
        if (!entry.class_id) { errors.class_id = 'Select a class'; valid = false; }
        if (!entry.subject_id) { errors.subject_id = 'Select a subject'; valid = false; }
        return { ...entry, errors };
      });
      setClassEntries(validated);
      if (!valid) { 
        toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please fill all required fields', life: 3000 }); 
        return; 
      }
      
      // ✅ Build payload with session_id and session
      payload = {
        assignments: validated.map(entry => ({
          session_id: sessionId,
          session: sessionName,
          class_id: entry.class_id,
          sections: entry.section ? [entry.section] : [],
          teacher_id: sharedTeacherId,
          subject_id: entry.subject_id,
        })),
      };
    }

    console.log('📤 Final Payload:', JSON.stringify(payload, null, 2));

    setGlobalLoading(true);
    try {
      const res = await apiClient.post('/api/assigned-teachers/bulk-assign', payload);
      console.log('📥 Response:', res.data);
      
      if (res.data.status === 'success') {
        const results: any[] = res.data.results || [];
        const successCount = results.filter(r => r.status === 'success').length;
        const errorCount = results.filter(r => r.status === 'error').length;
        if (errorCount === 0) {
          toast.current?.show({ severity: 'success', summary: 'Done!', detail: `${successCount} assignment(s) saved.`, life: 4000 });
          setTimeout(() => { onSuccess(); handleClose(); }, 300);
        } else if (successCount > 0) {
          toast.current?.show({ severity: 'warn', summary: 'Partial Success', detail: `${successCount} succeeded, ${errorCount} failed.`, life: 5000 });
          console.log('⚠️ Failed assignments:', results.filter(r => r.status === 'error'));
        } else {
          toast.current?.show({ severity: 'error', summary: 'Failed', detail: `All ${errorCount} assignments failed.`, life: 4000 });
        }
      } else {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: res.data.message || 'Submission failed', life: 4000 });
      }
    } catch (err: any) {
      console.error('❌ Submission error:', err.response?.data || err.message);
      toast.current?.show({ 
        severity: 'error', 
        summary: 'Error', 
        detail: err.response?.data?.message || 'Submission failed', 
        life: 4000 
      });
    } finally {
      setGlobalLoading(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  /* ─── Derived ──────────────────────────────────────────────────── */
  const classDropdownOptions = classOptions.filter(o => o.value !== '').map(o => ({ label: o.label, value: o.value }));
  const teacherDropdownOptions = teachers.map(t => ({ label: t.name, value: t.id }));
  const entryCount = mode === 'separate' ? assignments.length : classEntries.length;

  /* ─── Header ───────────────────────────────────────────────────── */
  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '8px 0' }}>
      <div
        style={{
          width: '54px',
          height: '54px',
          borderRadius: '14px',
          background: 'linear-gradient(135deg, #1e3a8a 0%, #4169E1 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '24px',
          fontWeight: 700,
          boxShadow: '0 8px 24px rgba(65,105,225,0.3)',
          flexShrink: 0,
          border: '3px solid white',
        }}
      >
        <i className="pi pi-user-plus" />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e40af', lineHeight: 1.2, marginBottom: '4px' }}>
          Assign Teachers
        </div>
        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
          {entryCount} assignment{entryCount !== 1 ? 's' : ''} configured
        </div>
      </div>
    </div>
  );

  /* ══════════════════════════════ RENDER ══════════════════════════ */
  return (
    <>
      <Toast ref={toast} position="top-right" />

      <style>{`
        .p-dialog-mask.assign-modal {
          backdrop-filter: blur(4px);
          background-color: rgba(0, 0, 0, 0.25) !important;
          animation: bmMaskFadeIn 0.35s ease;
        }
        .p-dialog-mask.assign-modal.mask-closing {
          animation: bmMaskFadeOut 0.4s ease !important;
        }
        @keyframes bmMaskFadeIn {
          from { opacity: 0; background-color: rgba(0, 0, 0, 0); }
          to { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
        }
        @keyframes bmMaskFadeOut {
          from { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
          to { opacity: 0; background-color: rgba(0, 0, 0, 0); }
        }
        .assign-modal .p-dialog {
          border-radius: 24px !important;
          overflow: hidden;
          animation: bmDialogOpen 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
        }
        .assign-modal .p-dialog.dialog-closing {
          animation: bmDialogClose 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        @keyframes bmDialogOpen {
          from { opacity: 0; transform: scale(0.92) translateY(-20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes bmDialogClose {
          from { opacity: 1; transform: scale(1) translateY(0); }
          to { opacity: 0; transform: scale(0.88) translateY(25px); }
        }
        .assign-modal .p-dialog-header {
          padding: 20px 24px !important;
          border-bottom: 1px solid #f1f5f9 !important;
          background: #ffffff !important;
          border-radius: 24px 24px 0 0 !important;
          border-left: 12px solid rgba(255, 255, 255, 0.8) !important;
          border-right: 12px solid rgba(255, 255, 255, 0.8) !important;
          border-top: 12px solid rgba(255, 255, 255, 0.8) !important;
          margin: -12px -12px 0 -12px !important;
        }
        .assign-modal .p-dialog-content {
          border: 12px solid rgba(255, 255, 255, 0.8) !important;
          border-top: none !important;
          border-radius: 0 0 24px 24px !important;
        }
        .assign-modal .p-dialog-content::-webkit-scrollbar { width: 8px; }
        .assign-modal .p-dialog-content::-webkit-scrollbar-track { background: rgba(241, 245, 249, 0.5); border-radius: 10px; }
        .assign-modal .p-dialog-content::-webkit-scrollbar-thumb { background: rgba(148, 163, 184, 0.5); border-radius: 10px; }
        .assign-modal .p-dialog-content::-webkit-scrollbar-thumb:hover { background: rgba(100, 116, 139, 0.7); }

        .assign-modal .p-dialog-header-icons .p-dialog-header-close {
          width: 36px !important; height: 36px !important;
          border-radius: 10px !important; color: #94a3b8 !important;
          border: 1px solid #e5e7eb !important; transition: all 0.2s !important;
        }
        .assign-modal .p-dialog-header-icons .p-dialog-header-close:hover {
          background: #fee2e2 !important; color: #ef4444 !important;
          border-color: #fecaca !important;
        }

        .bm-mode-toggle {
          display: flex; gap: 0; border-radius: 12px; overflow: hidden;
          border: 1.5px solid rgba(65,105,225,0.2);
          background: rgba(65,105,225,0.04);
          margin-bottom: 20px;
        }
        .bm-mode-btn {
          flex: 1; padding: 10px 16px; border: none; cursor: pointer;
          font-size: 13px; font-weight: 600; transition: all 0.2s;
          display: flex; align-items: center; justify-content: center; gap: 7px;
          background: transparent; color: #6b7280;
        }
        .bm-mode-btn.active {
          background: linear-gradient(135deg, #1e3a8a 0%, #4169E1 100%);
          color: #fff; box-shadow: 0 2px 10px rgba(65,105,225,0.35);
        }

        .bm-card {
          background: rgba(255,255,255,0.8);
          border: 1px solid rgba(59,130,246,0.1); border-radius: 16px;
          padding: 20px; margin-bottom: 14px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.05);
          transition: box-shadow 0.2s;
          animation: bm-slide-in 0.25s ease-out;
        }
        .bm-card:hover { box-shadow: 0 6px 20px rgba(65,105,225,0.1); }
        @keyframes bm-slide-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .bm-card-header {
          display: flex; align-items: center; justify-content: space-between;
          gap: 10px; margin-bottom: 16px; padding-bottom: 12px;
          border-bottom: 1px solid rgba(229, 231, 235, 0.6);
        }
        .bm-card-header-left { display: flex; align-items: center; gap: 10px; min-width: 0; }
        .bm-card-icon {
          display: flex; align-items: center; justify-content: center;
          width: 32px; height: 32px; border-radius: 8px; flex-shrink: 0;
          background: linear-gradient(135deg, #1e3a8a 0%, #4169E1 100%);
          color: white;
        }
        .bm-card-title {
          color: #1e3a8a; font-weight: 700; font-size: 14px;
          letter-spacing: 0.02em; text-transform: uppercase;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .bm-card-remove {
          width: 32px; height: 32px; border-radius: 8px; border: 1px solid #fecaca;
          background: #fee2e2; color: #ef4444; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; flex-shrink: 0; transition: all 0.15s;
        }
        .bm-card-remove:hover:not(:disabled) { background: #fecaca; }
        .bm-card-remove:disabled { opacity: 0.5; cursor: not-allowed; }

        .bm-label {
          font-size: 11px; font-weight: 700; color: #6b7280;
          text-transform: uppercase; letter-spacing: 0.7px;
          margin-bottom: 8px; display: flex; align-items: center; gap: 6px;
        }
        .bm-label-req { color: #ef4444; }
        .bm-error { font-size: 11px; color: #ef4444; margin-top: 4px; display: block; }

        .bm-chip {
          display: inline-flex; align-items: center; gap: 6px;
          background: #f9fafb; border: 1.5px solid #e5e7eb;
          border-radius: 8px; padding: 6px 12px;
          font-size: 13px; font-weight: 500; color: #374151;
          cursor: pointer; transition: all 0.15s; user-select: none;
          white-space: nowrap;
        }
        .bm-chip:hover { border-color: #4169E1; background: rgba(65,105,225,0.04); }
        .bm-chip.selected {
          background: linear-gradient(135deg, rgba(65,105,225,0.13) 0%, rgba(91,124,230,0.08) 100%);
          border-color: #4169E1; color: #1e40af; font-weight: 600;
        }

        .bm-chip-check {
          display: inline-flex; align-items: center; gap: 6px;
          background: #f9fafb; border: 1.5px solid #e5e7eb;
          border-radius: 8px; padding: 6px 12px;
          font-size: 13px; font-weight: 500; color: #374151;
          cursor: pointer; transition: all 0.15s; user-select: none;
          white-space: nowrap;
        }
        .bm-chip-check:hover { border-color: #4169E1; background: rgba(65,105,225,0.04); }
        .bm-chip-check.selected {
          background: linear-gradient(135deg, rgba(65,105,225,0.13) 0%, rgba(91,124,230,0.08) 100%);
          border-color: #4169E1; color: #1e40af; font-weight: 600;
        }

        .bm-radio-dot {
          width: 14px; height: 14px; border-radius: 50%;
          border: 2px solid #d1d5db;
          display: inline-flex; align-items: center; justify-content: center;
          transition: all 0.15s; flex-shrink: 0;
        }
        .bm-chip.selected .bm-radio-dot {
          border-color: #4169E1; background: #4169E1;
          box-shadow: 0 0 0 3px rgba(65,105,225,0.18);
        }
        .bm-radio-dot-inner {
          width: 5px; height: 5px; border-radius: 50%; background: #fff;
        }

        .bm-check-dot {
          width: 14px; height: 14px; border-radius: 3px;
          border: 2px solid #d1d5db;
          display: inline-flex; align-items: center; justify-content: center;
          transition: all 0.15s; flex-shrink: 0;
        }
        .bm-chip-check.selected .bm-check-dot {
          border-color: #4169E1; background: #4169E1;
        }

        .bm-teacher-banner {
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          border: 1.5px solid #bfdbfe; border-radius: 14px;
          padding: 16px 20px; margin-bottom: 20px;
          display: flex; align-items: center; gap: 14px;
        }
        .bm-teacher-icon {
          width: 40px; height: 40px; border-radius: 10px;
          background: linear-gradient(135deg, #1e3a8a, #4169E1);
          display: flex; align-items: center; justify-content: center;
          color: white; font-size: 18px; flex-shrink: 0;
        }

        .bm-subsection {
          background: rgba(248,250,252,0.8);
          border: 1px solid rgba(65,105,225,0.08);
          border-radius: 12px; padding: 14px; margin-top: 12px;
        }
        .bm-subsection-title {
          font-size: 11px; font-weight: 700; color: #6b7280;
          text-transform: uppercase; letter-spacing: 0.6px;
          margin-bottom: 10px; display: flex; align-items: center; gap: 6px;
        }

        .bm-add-btn {
          width: 100%; padding: 12px; border-radius: 12px;
          border: 2px dashed rgba(65,105,225,0.3);
          background: rgba(65,105,225,0.03); color: #4169E1;
          font-size: 13px; font-weight: 600; cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          transition: all 0.2s; margin-top: 4px;
        }
        .bm-add-btn:hover:not(:disabled) {
          border-color: #4169E1; background: rgba(65,105,225,0.06); transform: translateY(-1px);
        }
        .bm-add-btn:disabled { opacity: 0.5; cursor: not-allowed; }

        .bm-reset-btn {
          padding: 12px 20px; border-radius: 10px;
          border: 2px solid #e5e7eb; background: #fff;
          color: #6b7280; font-weight: 600; font-size: 13px;
          cursor: pointer; transition: all 0.2s;
          display: flex; align-items: center; gap: 8px;
        }
        .bm-reset-btn:hover:not(:disabled) { border-color: #cbd5e1; background: #f9fafb; }
        .bm-reset-btn:disabled { opacity: 0.5; cursor: not-allowed; }

        .bm-submit-btn {
          flex: 1; padding: 14px 24px; border-radius: 12px;
          border: none; cursor: pointer;
          background: linear-gradient(135deg, #1e3a8a 0%, #4169E1 100%);
          color: white; font-size: 14px; font-weight: 700;
          box-shadow: 0 6px 20px rgba(65,105,225,0.35);
          transition: all 0.3s; display: flex; align-items: center; justify-content: center; gap: 10px;
        }
        .bm-submit-btn:hover:not(:disabled) {
          transform: translateY(-2px); box-shadow: 0 10px 28px rgba(65,105,225,0.45);
        }
        .bm-submit-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

        .assign-modal .p-dropdown {
          border-radius: 10px !important; border: 1.5px solid #e5e7eb !important;
          width: 100% !important; transition: border-color 0.2s, box-shadow 0.2s !important;
        }
        .assign-modal .p-dropdown:not(.p-disabled):hover { border-color: #4169E1 !important; }
        .assign-modal .p-dropdown:not(.p-disabled).p-focus {
          border-color: #4169E1 !important; box-shadow: 0 0 0 3px rgba(65,105,225,0.12) !important;
        }
        .assign-modal .p-dropdown .p-dropdown-label { font-size: 13px !important; color: #1f2937 !important; }
        .assign-modal .p-dropdown-item { font-size: 13px !important; }
        .assign-modal .p-dropdown.p-disabled { opacity: 0.6 !important; }

        .bm-chips-wrap { display: flex; flex-wrap: wrap; gap: 8px; }
        .bm-loading-row { display: flex; align-items: center; gap: 8px; color: #6b7280; font-size: 12px; margin-top: 8px; }
        .bm-empty-note { font-size: 12px; color: #9ca3af; font-style: italic; margin-top: 6px; }
      `}</style>

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: '1000px', borderRadius: '24px', overflow: 'hidden' }}
        contentStyle={{
          padding: '20px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 130px)',
          overflowY: 'auto',
        }}
        modal
        dismissableMask
        draggable={false}
        resizable={false}
        blockScroll
        maskClassName="assign-modal"
      >
        {/* ── Teacher loading banner ── */}
        {teachersLoading && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: '#eff6ff', border: '1px solid #bfdbfe',
            borderRadius: 10, padding: '12px 16px', marginBottom: 16,
          }}>
            <ProgressSpinner style={{ width: 18, height: 18 }} strokeWidth="4" />
            <span style={{ fontSize: 13, color: '#1d4ed8', fontWeight: 600 }}>Loading teachers list…</span>
          </div>
        )}

        {/* ══════ MODE TOGGLE ══════ */}
        <div className="bm-mode-toggle">
          <button
            className={`bm-mode-btn${mode === 'shared' ? ' active' : ''}`}
            onClick={() => switchMode('shared')}
          >
            <i className="pi pi-user" />
            One Teacher, Multiple Classes
          </button>
          <button
            className={`bm-mode-btn${mode === 'separate' ? ' active' : ''}`}
            onClick={() => switchMode('separate')}
          >
            <i className="pi pi-users" />
            Separate Teacher per Entry
          </button>
        </div>

        {/* ════════════════════════════════════════════════
            MODE A — shared teacher
        ════════════════════════════════════════════════ */}
        {mode === 'shared' && (
          <>
            {/* Global teacher picker */}
            <div className="bm-teacher-banner">
              <div className="bm-teacher-icon"><i className="pi pi-user" /></div>
              <div style={{ flex: 1 }}>
                <div className="bm-label" style={{ marginBottom: 6 }}>
                  Select Teacher <span className="bm-label-req">*</span>
                </div>
                <Dropdown
                  value={sharedTeacherId}
                  options={teacherDropdownOptions}
                  onChange={e => setSharedTeacherId(e.value)}
                  placeholder={teachersLoading ? 'Loading…' : 'Choose a teacher for all entries'}
                  disabled={teachersLoading || teachers.length === 0}
                  filter
                  filterPlaceholder="Search teacher…"
                  style={{ minWidth: 260 }}
                />
              </div>
            </div>

            {/* Class entries */}
            {classEntries.map((entry, idx) => {
              const cls = classOptions.find(o => o.value === entry.selectedClassValue) as ClassOption | undefined;
              const sectionOptions: string[] = cls?.sections || [];
              const subjects: SubjectData[] = subjectsByEntry[idx] || [];
              const isSubjLoading = subjectsLoadingEntry[idx] || false;

              return (
                <div key={idx} className="bm-card">
                  <div className="bm-card-header">
                    <div className="bm-card-header-left">
                      <div className="bm-card-icon">
                        <i className="pi pi-bookmark" style={{ fontSize: 14 }} />
                      </div>
                      <span className="bm-card-title">Entry {idx + 1}</span>
                    </div>
                    {classEntries.length > 1 && (
                      <button
                        className="bm-card-remove"
                        onClick={() => removeEntry(idx)}
                        disabled={globalLoading}
                      >
                        <i className="pi pi-times" />
                      </button>
                    )}
                  </div>

                  <div className="bm-label">
                    Class <span className="bm-label-req">*</span>
                  </div>
                  <div className="bm-chips-wrap">
                    {classDropdownOptions.map(opt => {
                      const selected = entry.selectedClassValue === opt.value;
                      return (
                        <div
                          key={opt.value}
                          className={`bm-chip${selected ? ' selected' : ''}`}
                          onClick={() => !globalLoading && handleEntryClassChip(idx, opt.value)}
                        >
                          <span className="bm-radio-dot">
                            {selected && <span className="bm-radio-dot-inner" />}
                          </span>
                          {opt.label}
                        </div>
                      );
                    })}
                  </div>
                  {entry.errors.class_id && <span className="bm-error">{entry.errors.class_id}</span>}

                  {entry.class_id && sectionOptions.length > 0 && (
                    <div className="bm-subsection">
                      <div className="bm-subsection-title">
                        <i className="pi pi-th-large" style={{ fontSize: 11 }} />
                        Section
                        <span style={{ color: '#9ca3af', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                          &nbsp;(optional — blank = all)
                        </span>
                      </div>
                      <div className="bm-chips-wrap">
                        {sectionOptions.map(sec => {
                          const selected = entry.section === sec;
                          return (
                            <div
                              key={sec}
                              className={`bm-chip${selected ? ' selected' : ''}`}
                              onClick={() => !globalLoading && updateEntry(idx, { section: selected ? '' : sec })}
                            >
                              <span className="bm-radio-dot">
                                {selected && <span className="bm-radio-dot-inner" />}
                              </span>
                              Section {sec}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {entry.class_id && (
                    <div className="bm-subsection" style={{ marginTop: 10 }}>
                      <div className="bm-subsection-title">
                        <i className="pi pi-book" style={{ fontSize: 11 }} />
                        Subject <span className="bm-label-req" style={{ textTransform: 'none', letterSpacing: 0 }}>*</span>
                      </div>
                      {isSubjLoading ? (
                        <div className="bm-loading-row">
                          <ProgressSpinner style={{ width: 14, height: 14 }} strokeWidth="4" />
                          Loading subjects…
                        </div>
                      ) : subjects.length === 0 ? (
                        <span className="bm-empty-note">No subjects found for this class.</span>
                      ) : (
                        <div className="bm-chips-wrap">
                          {subjects.map(s => {
                            const selected = entry.subject_id === s.subject_id;
                            return (
                              <div
                                key={s.subject_id}
                                className={`bm-chip${selected ? ' selected' : ''}`}
                                onClick={() => !globalLoading && updateEntry(idx, { subject_id: selected ? '' : s.subject_id })}
                              >
                                <span className="bm-radio-dot">
                                  {selected && <span className="bm-radio-dot-inner" />}
                                </span>
                                {s.subject_name}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {entry.errors.subject_id && <span className="bm-error">{entry.errors.subject_id}</span>}
                    </div>
                  )}
                </div>
              );
            })}

            <button className="bm-add-btn" onClick={addEntry} disabled={globalLoading}>
              <i className="pi pi-plus-circle" />
              Add Another Class Entry
            </button>
          </>
        )}

        {/* ════════════════════════════════════════════════
            MODE B — separate teacher per entry
        ════════════════════════════════════════════════ */}
        {mode === 'separate' && assignments.map((row, idx) => {
          const cls = classOptions.find(o => o.value === row.selectedClassValue) as ClassOption | undefined;
          const sectionOptions: string[] = cls?.sections || [];
          const subjects: SubjectData[] = subjectsByRow[idx] || [];
          const isSubjLoading = subjectsLoading[idx] || false;

          return (
            <div key={idx} className="bm-card">
              <div className="bm-card-header">
                <div className="bm-card-header-left">
                  <div className="bm-card-icon">
                    <i className="pi pi-bookmark" style={{ fontSize: 14 }} />
                  </div>
                  <span className="bm-card-title">Assignment {idx + 1}</span>
                </div>
                {assignments.length > 1 && (
                  <button
                    className="bm-card-remove"
                    onClick={() => removeRow(idx)}
                    disabled={globalLoading}
                  >
                    <i className="pi pi-times" />
                  </button>
                )}
              </div>

              <div style={{ marginBottom: 16 }}>
                <div className="bm-label">
                  Teacher <span className="bm-label-req">*</span>
                </div>
                <Dropdown
                  value={row.teacher_id}
                  options={teacherDropdownOptions}
                  onChange={e => updateRow(idx, { teacher_id: e.value })}
                  placeholder={teachersLoading ? 'Loading…' : teachers.length === 0 ? 'No teachers' : 'Select a Teacher'}
                  disabled={teachersLoading || teachers.length === 0}
                  filter
                  filterPlaceholder="Search teacher…"
                />
                {row.errors.teacher_id && <span className="bm-error">{row.errors.teacher_id}</span>}
              </div>

              <div className="bm-label">
                Class <span className="bm-label-req">*</span>
              </div>
              <div className="bm-chips-wrap">
                {classDropdownOptions.map(opt => {
                  const selected = row.selectedClassValue === opt.value;
                  return (
                    <div
                      key={opt.value}
                      className={`bm-chip${selected ? ' selected' : ''}`}
                      onClick={() => !globalLoading && handleRowClassChange(idx, selected ? '' : opt.value)}
                    >
                      <span className="bm-radio-dot">
                        {selected && <span className="bm-radio-dot-inner" />}
                      </span>
                      {opt.label}
                    </div>
                  );
                })}
              </div>
              {row.errors.class_id && <span className="bm-error">{row.errors.class_id}</span>}

              {row.class_id && sectionOptions.length > 0 && (
                <div className="bm-subsection">
                  <div className="bm-subsection-title">
                    <i className="pi pi-th-large" style={{ fontSize: 11 }} />
                    Sections
                    <span style={{ color: '#9ca3af', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                      &nbsp;(optional — blank = all)
                    </span>
                  </div>
                  <div className="bm-chips-wrap">
                    {sectionOptions.map(sec => {
                      const checked = row.sections.includes(sec);
                      return (
                        <div
                          key={sec}
                          className={`bm-chip-check${checked ? ' selected' : ''}`}
                          onClick={() => !globalLoading && handleSectionToggle(idx, sec, !checked)}
                        >
                          <span className="bm-check-dot">
                            {checked && <i className="pi pi-check" style={{ fontSize: 8, color: '#fff' }} />}
                          </span>
                          Section {sec}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {row.class_id && (
                <div className="bm-subsection" style={{ marginTop: 10 }}>
                  <div className="bm-subsection-title">
                    <i className="pi pi-book" style={{ fontSize: 11 }} />
                    Subject <span className="bm-label-req" style={{ textTransform: 'none', letterSpacing: 0 }}>*</span>
                  </div>
                  {isSubjLoading ? (
                    <div className="bm-loading-row">
                      <ProgressSpinner style={{ width: 14, height: 14 }} strokeWidth="4" />
                      Loading subjects…
                    </div>
                  ) : subjects.length === 0 ? (
                    <span className="bm-empty-note">No subjects found for this class.</span>
                  ) : (
                    <div className="bm-chips-wrap">
                      {subjects.map(s => {
                        const selected = row.subject_id === s.subject_id;
                        return (
                          <div
                            key={s.subject_id}
                            className={`bm-chip${selected ? ' selected' : ''}`}
                            onClick={() => !globalLoading && updateRow(idx, { subject_id: selected ? '' : s.subject_id })}
                          >
                            <span className="bm-radio-dot">
                              {selected && <span className="bm-radio-dot-inner" />}
                            </span>
                            {s.subject_name}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {row.errors.subject_id && <span className="bm-error">{row.errors.subject_id}</span>}
                </div>
              )}
            </div>
          );
        })}

        {mode === 'separate' && (
          <button className="bm-add-btn" onClick={addRow} disabled={globalLoading}>
            <i className="pi pi-plus-circle" />
            Add Another Assignment
          </button>
        )}

        {/* ── Actions ── */}
        <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
          <button
            className="bm-reset-btn"
            onClick={resetAll}
            disabled={globalLoading}
          >
            <i className="pi pi-refresh" />
            Reset
          </button>
          <button
            className="bm-submit-btn"
            onClick={handleSubmit}
            disabled={globalLoading || teachersLoading}
          >
            {globalLoading
              ? <><i className="pi pi-spin pi-spinner" /> Assigning…</>
              : <><i className="pi pi-check" /> Assign {entryCount} Teacher{entryCount !== 1 ? 's' : ''}</>
            }
          </button>
        </div>
      </Dialog>
    </>
  );
};

export default AssignBulkTeachersModal;